import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import usePlatformStats from '../hooks/usePlatformStats';
import { formatStatNumber } from '../utils/formatStatNumber';
import './HeroStats.css';

const STAT_SLOTS = [
  { key: 'totalArticles', label: 'Articles Archived' },
  { key: 'activeSources', label: 'Active Sources' },
  { key: 'recent24h', label: 'Last 24 Hours' },
];

function useDeferredEnabled() {
  const containerRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setEnabled(true);
            observer.disconnect();
          }
        },
        { rootMargin: '100px' }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }

    const scheduleIdle =
      window.requestIdleCallback ||
      ((cb) => window.setTimeout(cb, 200));
    const cancelIdle =
      window.cancelIdleCallback ||
      ((id) => window.clearTimeout(id));

    const idleId = scheduleIdle(() => setEnabled(true));
    return () => cancelIdle(idleId);
  }, []);

  return { containerRef, enabled };
}

function AnimatedStatValue({ value, loading }) {
  const [displayValue, setDisplayValue] = useState(null);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (loading || value == null) {
      setDisplayValue(null);
      return;
    }

    if (prefersReducedMotion.current) {
      setDisplayValue(value);
      return;
    }

    const duration = 200;
    const start = performance.now();
    const from = 0;
    const to = value;

    let frameId;
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const current = Math.round(from + (to - from) * progress);
      setDisplayValue(current);
      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      }
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [value, loading]);

  if (loading || displayValue == null) {
    return '—';
  }

  return formatStatNumber(displayValue);
}

const HeroStats = () => {
  const { containerRef, enabled } = useDeferredEnabled();
  const stats = usePlatformStats({ enabled });
  const showStatsLink = !stats.loading && Boolean(stats.error);

  return (
    <div
      ref={containerRef}
      className="hero-stats"
      aria-busy={stats.loading}
      aria-live="polite"
    >
      {STAT_SLOTS.map(({ key, label }) => (
        <Link key={key} to="/stats" className="stat-item" title="View live stats">
          <span className="stat-number">
            <AnimatedStatValue value={stats[key]} loading={stats.loading} />
          </span>
          <span className="stat-label">{label}</span>
        </Link>
      ))}
      {showStatsLink && (
        <p className="hero-stats-fallback">
          <Link to="/stats">View live stats →</Link>
        </p>
      )}
    </div>
  );
};

export default HeroStats;
